import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import AccessingMessage from "../accessingMessage/AccessingMessage";
import UrlParser from "js-video-url-parser";
import VideoElement from "../player/VideoElement";
import ImageElement from "../player/ImageElement";
import LinkElement from "../player/LinkElement";
import {
  firstTimeStatus,
  playingStatus,
  updateElementIndex,
  updateFirstVideoInteraction,
} from "../../reducers/players/playersSlice";
import PlayLogo from "../../assets/play.png";
import { Box, Typography } from "@mui/material";
import MessageElement from "../player/MessageElement";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { Swiper, SwiperSlide } from "swiper/react";
import { Virtual, EffectCube, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cube";
import "swiper/css/pagination";
import "swiper/css/virtual";
import SpaceObject from "../player/SpaceObject";

const PREFIX = "StoriesDisplayer";

const classes = {
  progressContainer: `${PREFIX}-progressContainer`,
  playerContainer: `${PREFIX}-playerContainer`,
  playOverlay: `${PREFIX}-playOverlay`,
  videoPlayButton: `${PREFIX}-videoPlayButton`,
  playButtonImage: `${PREFIX}-playButtonImage`,
  initialMessage: `${PREFIX}-initialMessage`,
  stepDisplayer: `${PREFIX}-stepDisplayer`,
  stepArrow: `${PREFIX}-stepArrow`,
  stepCount: `${PREFIX}-stepCount`,
  // videoPlayer: `${PREFIX}-videoPlayer`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.playerContainer}`]: {
    position: "absolute",
    height: "100%",
    overflow: "hidden",
    width: "100%",
    top: 0,
    left: 0,
    msOverflowStyle: "none",
    "@media (orientation: landscape)": {
      height: "100dvh",
      minHeight: "100dvh",
    },
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  [`& .${classes.playOverlay}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    zIndex: 99,
  },
  [`& .${classes.videoPlayButton}`]: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    "& > .MuiSvgIcon-root": {
      fontSize: "4rem !important",
      cursor: "pointer",
    },
  },
  [`& .${classes.playButtonImage}`]: {
    maxWidth: "300px",
    width: "100%",
  },
  [`& .${classes.initialMessage}`]: {
    margin: "1.25rem auto 0",
    maxWidth: "min(92%, 420px)",
    color: "rgba(255, 255, 255, 0.92)",
    fontSize: "0.95rem",
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 4,
    overflow: "hidden",
    pointerEvents: "none",
  },
  [`& .${classes.stepDisplayer}`]: {
    position: "absolute",
    left: theme.spacing(1.5),
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.25),
    color: "rgba(255, 255, 255, 0.92)",
    pointerEvents: "none",
    textAlign: "center",
  },
  [`& .${classes.stepArrow}`]: {
    width: 22,
    height: 22,
    fontSize: 22,
    opacity: 0.88,
    display: "block",
  },
  [`& .${classes.stepCount}`]: {
    fontSize: "0.75rem",
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: "0.02em",
    minWidth: "2.75rem",
  },
  // [`& .${classes.videoPlayer}`]: {
  //   backgroundColor: "black",
  //   height: "100%",
  //   width: "100%",
  //   objectFit: "contain",
  // },
}));

const StoriesDisplayer = ({ token, direction }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [swiperRef, setSwiperRef] = useState(null);
  const [slideChanging, setSlideChanging] = useState(false);

  const playlist = useSelector((state) => state.players.playlist);
  const currentIndex = useSelector(
    (state) => state.players.currentPlayingIndex
  );
  const error = useSelector((state) => state.players.error);
  const loading = useSelector((state) => state.players.loading);
  const firstTime = useSelector((state) => state.players.firstTime);
  const playing = useSelector((state) => state.players.playing);
  const openingItem = playlist?.[currentIndex] || playlist?.[0];
  const openingMessage =
    openingItem &&
    openingItem.type !== "message" &&
    openingItem.message &&
    String(openingItem.message).trim() !== ""
      ? String(openingItem.message).trim()
      : "";

  // const progressCircle = useRef(null);
  // const progressContent = useRef(null);

  // const onAutoplayTimeLeft = (s, time, progress, duration) => {
  //   const swiper = document.querySelector(".swiper").swiper;
  //   if (!firstTime) {
  //     progressCircle.current.style.setProperty(
  //       "--progress",
  //       1 - swiper.autoplay.timeLeft / (duration * 1000)
  //     );
  //   }
  //   progressContent.current.textContent = `${Math.ceil(time / 1000)}s`;
  // };

  const handleGoBackToScan = () => window.history.back();

  const accessingMessagesComponents = useMemo(
    () => ({
      "token-error": (
        <AccessingMessage
          text={t("tokenError")}
          error
          onGoBack={handleGoBackToScan}
          goBackLabel={t("goBackToScan")}
        />
      ),
      "token-expired": (
        <AccessingMessage
          text={t("tokenExpired")}
          error
          onGoBack={handleGoBackToScan}
          goBackLabel={t("goBackToScan")}
        />
      ),
      unknown: <AccessingMessage text={t("unknownError")} error />,
      fetching: <AccessingMessage text={t("fetching")} loader />,
      noResults: <AccessingMessage text={t("noResults")} />,
    }),
    [t]
  );

  useEffect(() => {
    if (!firstTime && direction !== "vertical") {
      swiperRef.autoplay.start();
      swiperRef.autoplay.resume();
    } else {
      if (swiperRef) {
        swiperRef.autoplay.paused = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstTime, swiperRef]);

  useEffect(() => {
    if (swiperRef) {
      if (slideChanging) swiperRef.autoplay.resume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideChanging]);

  useEffect(() => {
    if (!firstTime) {
      if (!playing) {
        // swiperRef.autoplay.stop();
        swiperRef.autoplay.pause();
      } else {
        // swiperRef.autoplay.start();
        swiperRef.autoplay.resume();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, swiperRef]);

  const myVideoTag = useMemo(() => {
    var video = document.createElement("video");

    video.src = null;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    video.muted = false;
    video.preload = "auto";
    video.style.backgroundColor = "black";
    video.style.height = "100%";
    video.style.width = "100%";
    video.style.objectFit = "contain";
    video.addEventListener(
      "click",
      direction !== "vertical"
        ? undefined
        : (e) => {
            if (video.paused) {
              dispatch(playingStatus(true));
              video.play();
            } else {
              dispatch(playingStatus(false));
              video.pause();
            }
          }
    );
    return video;
  }, [dispatch, direction]);

  if (error) {
    return (
      accessingMessagesComponents[error] ??
      accessingMessagesComponents.unknown
    );
  } else if (loading || !playlist || !playlist[currentIndex]) {
    return accessingMessagesComponents.fetching;
  } else if (playlist && playlist.length === 0) {
    return accessingMessagesComponents.noResults;
  } else {
    return (
      <StyledDiv>
        {firstTime && (
          <div className={classes.playOverlay}>
            <div className={classes.videoPlayButton}>
              <img
                src={PlayLogo}
                alt="Play Icon"
                className={classes.playButtonImage}
                onClick={() => {
                  if (playlist[0].type === "video") {
                    const videoTag = document.querySelector("video");
                    videoTag.autoplay = true;
                    videoTag.muted = false;
                    videoTag.play();
                  }
                  //  else if (width > 768) {
                  //   // it's desktop so no need for locking firstInteraction
                  // }
                  dispatch(updateFirstVideoInteraction());
                  dispatch(playingStatus(true));
                  dispatch(firstTimeStatus());
                }}
              />
              {openingMessage ? (
                <Typography component="p" className={classes.initialMessage}>
                  {openingMessage}
                </Typography>
              ) : null}
            </div>
          </div>
        )}
        <Box
          className={classes.playerContainer}
          sx={{
            filter: firstTime ? "blur(6px)" : "none",
          }}
        >
          {direction === "vertical" && (
            <div className={classes.stepDisplayer}>
              <KeyboardArrowUpIcon className={classes.stepArrow} />
              <Typography component="span" className={classes.stepCount}>
                {currentIndex + 1}/{playlist.length}
              </Typography>
              <KeyboardArrowDownIcon className={classes.stepArrow} />
            </div>
          )}
          <Swiper
            onSlideChange={(elem) => {
              setSlideChanging(true);
              dispatch(updateElementIndex(elem.activeIndex));
            }}
            onSlideChangeTransitionEnd={() => setSlideChanging(false)}
            onSlideResetTransitionStart={() => {
              setSlideChanging(false);
            }}
            onSlideResetTransitionEnd={() => {
              setSlideChanging(false);
            }}
            onInit={() => {
              const inter = setInterval(() => {
                const swiper = document.querySelector(".swiper").swiper;
                if (swiper) {
                  setSwiperRef(swiper);
                  swiper.autoplay.stop();
                  clearInterval(inter);
                }
              }, 100);
            }}
            effect={direction === "vertical" ? "" : "cube"}
            grabCursor={true}
            cubeEffect={{
              shadow: true,
              slideShadows: true,
              shadowOffset: 20,
              shadowScale: 0.94,
            }}
            // autoplay={{
            //   delay: parseInt(playlist[currentIndex].duration) * 1000 || 8000,
            //   disableOnInteraction: false,
            //   pauseOnMouseEnter: true,
            // }}
            autoplay={false}
            modules={
              direction === "vertical"
                ? [Autoplay, Virtual]
                : [EffectCube, Autoplay, Pagination, Virtual]
            }
            // onAutoplayTimeLeft={!firstTime && playing ?
            //   (s, time, progress) => onAutoplayTimeLeft(s, time, progress, playlist[currentIndex].duration ? parseInt(playlist[currentIndex].duration) : 8)
            //   : undefined
            // }
            pagination={
              direction === "vertical"
                ? false
                : {
                    dynamicBullets: playlist.length > 15,
                    dynamicMainBullets: 1,
                  }
            }
            direction={direction}
            // virtual={{
            //   enabled: true,
            //   cache: true,
            //   // addSlidesAfter: 5,
            // }}
            className="mySwiper"
          >
            {playlist.map((item, index) => {
              const duration = parseInt(item.duration) * 1000;
              const isFromProvider =
                item.targetUrl?.includes("googlevideo.com");
              if (
                item.type === "video" ||
                UrlParser.parse(item.targetUrl) ||
                isFromProvider
              ) {
                return (
                  <SwiperSlide
                    key={`swiper-slide-${index + 1}`}
                    data-swiper-autoplay={duration || "8000"}
                    virtualIndex={index}
                  >
                    {({ isActive }) => (
                      <VideoElement
                        url={item.targetUrl}
                        token={token}
                        currentIndex={index}
                        disableProgress={direction !== "vertical"}
                        isActive={isActive}
                        verticalPlayer={direction === "vertical"}
                        myVideoTag={myVideoTag}
                      />
                    )}
                  </SwiperSlide>
                );
              } else if (item.type === "image") {
                return (
                  <SwiperSlide
                    key={`swiper-slide-${index + 1}`}
                    data-swiper-autoplay={duration || "8000"}
                    virtualIndex={index}
                  >
                    {({ isActive }) => (
                      <ImageElement
                        key={`image-element-${index + 1}`}
                        url={item.targetUrl}
                        token={token}
                        currentIndex={index}
                        isActive={isActive}
                        verticalPlayer={direction === "vertical"}
                        myVideoTag={myVideoTag}
                      />
                    )}
                  </SwiperSlide>
                );
              } else if (item.type === "message") {
                return (
                  <SwiperSlide
                    key={`swiper-slide-${index + 1}`}
                    data-swiper-autoplay={duration || "8000"}
                    virtualIndex={index}
                  >
                    {({ isActive }) => (
                      <MessageElement
                        key={`link-element-${index + 1}`}
                        message={item.message}
                        token={token}
                        currentIndex={index}
                        isActive={isActive}
                        verticalPlayer={direction === "vertical"}
                        myVideoTag={myVideoTag}
                      />
                    )}
                  </SwiperSlide>
                );
              } else if (item.type === "model") {
                const { link, usdz, imageUrl, molding, size, type } =
                  item.model3D;
                return (
                  <SwiperSlide
                    key={`swiper-slide-${index + 1}`}
                    data-swiper-autoplay={duration || "8000"}
                    virtualIndex={index}
                  >
                    {({ isActive }) => (
                      <SpaceObject
                        key={`space-element-${index + 1}`}
                        url={link}
                        usdz={usdz}
                        token={token}
                        currentIndex={index}
                        isActive={isActive}
                        verticalPlayer={direction === "vertical"}
                        myVideoTag={myVideoTag}
                        isFrame={!!molding}
                        molding={molding}
                        size={size}
                        imageUrl={imageUrl}
                      />
                    )}
                  </SwiperSlide>
                );
              } else {
                return (
                  <SwiperSlide
                    key={`swiper-slide-${index + 1}`}
                    // data-swiper-autoplay={duration || "8000"}
                    virtualIndex={index}
                  >
                    {({ isActive }) => (
                      <LinkElement
                        key={`link-element-${index + 1}`}
                        url={item.targetUrl}
                        token={token}
                        currentIndex={index}
                        isActive={isActive}
                        verticalPlayer={direction === "vertical"}
                        myVideoTag={myVideoTag}
                      />
                    )}
                  </SwiperSlide>
                );
              }
            })}
            {/* <div className="autoplay-progress" slot="container-end" style={{display: direction === 'vertical' ? 'none' : 'flex'}}>
              <svg viewBox="0 0 48 48" ref={progressCircle}>
                {!slideChanging &&
                  <circle cx="24" cy="24" r="20"></circle>
                }
              </svg>
              <span ref={progressContent} style={{ display: slideChanging ? 'none' : 'block' }} />
            </div> */}
          </Swiper>
        </Box>
      </StyledDiv>
    );
  }
};

export default StoriesDisplayer;
